import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useCreditsRealtime } from './useCreditsRealtime';

interface SubscriptionData {
  subscribed: boolean;
  subscription_status: 'trialing' | 'active' | 'canceled' | 'past_due' | 'unpaid' | 'expired' | 'new' | null;
  subscription_tier: 'Plus' | 'Premium' | null;
  subscription_end_date?: string;
  trial_end_date?: string;
  payment_method_added?: boolean;
  stripe_customer_id?: string;
}

interface ExtendedSubscriptionData extends SubscriptionData {
  isTrialExpired: boolean;
  isPastDue: boolean;
  isCanceled: boolean;
  isActive: boolean;
  isOnTrial: boolean;
  needsPayment: boolean;
  isExpired: boolean;
  needsPaymentMethod: boolean;
  isInGracePeriod: boolean;
  hasPaymentIssues: boolean;
  gracePeriodDays?: number;
  trialDaysRemaining?: number;
  buttonText: string;
}

export const useSubscription = () => {
  const { user, forceSignOut } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const lastCheckRef = useRef<number>(0);
  const requestInProgressRef = useRef<boolean>(false);
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache - increased from 2 minutes
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);

  // Enable real-time updates for credits
  useCreditsRealtime();

  // Fetch subscription data from user_credits table with optimized cache settings
  const { data: subscriptionData, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['subscription-status', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const startTime = performance.now();
      console.log('🔍 Fetching subscription data for user:', user.id);

      const { data: userCredits, error } = await supabase
        .from('user_credits')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      const endTime = performance.now();
      console.log(`⏱️ Subscription data fetch took ${endTime - startTime}ms`);

      return userCredits;
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000, // 2 minutes - increased from 30 seconds
    gcTime: CACHE_DURATION * 2,
    refetchOnWindowFocus: false, // Disable to prevent excessive refetches
    refetchInterval: false, // Disable automatic refetching
    refetchOnMount: false, // Only fetch if no cached data exists
    retry: (failureCount, error) => {
      // Don't retry on auth errors to prevent loops
      if (error?.message?.includes('Session') || error?.message?.includes('authentication')) {
        return false;
      }
      return failureCount < 2;
    }
  });

  // Smart refresh with request deduplication
  const checkSubscription = useCallback(async (force = false) => {
    if (!user) return;

    const now = Date.now();

    // Request deduplication - prevent multiple simultaneous calls
    if (requestInProgressRef.current) {
      console.log('🚫 Subscription check already in progress, skipping');
      return;
    }

    // Cache check - use cached data if recent
    if (!force && (now - lastCheckRef.current) < CACHE_DURATION) {
      console.log('✅ Using cached subscription data');
      return;
    }

    console.log('🔄 Starting subscription check for user:', user.id);
    requestInProgressRef.current = true;
    const startTime = performance.now();

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');

      if (error) {
        console.error('❌ Error checking subscription:', error);

        // Check if it's an authentication error - but don't auto-logout
        if (error.message?.includes('Session') || error.message?.includes('authentication') || error.message?.includes('JWT')) {
          console.log('🔒 Authentication error detected in subscription check');
          return;
        }

        console.warn('⚠️ Subscription check failed, using cached data');
        return;
      }

      lastCheckRef.current = now;
      const endTime = performance.now();
      console.log(`⏱️ Subscription check completed in ${endTime - startTime}ms`);

      // Update local cache
      queryClient.setQueryData(['subscription-status', user.id], data);

    } catch (error) {
      console.error('❌ Error checking subscription:', error);

      // Check if it's an authentication error
      if (error && typeof error === 'object' && 'message' in error) {
        const errorMessage = (error as any).message;
        if (errorMessage?.includes('Session') || errorMessage?.includes('authentication') || errorMessage?.includes('JWT')) {
          console.log('🔒 Authentication error detected, forcing sign out');
          forceSignOut();
          return;
        }
      }
    } finally {
      requestInProgressRef.current = false;
    }
  }, [user, queryClient, forceSignOut]);

  // Enhance subscription data with computed properties
  const subscription: ExtendedSubscriptionData | null = subscriptionData ? {
    subscribed: subscriptionData.subscription_status === 'active' || subscriptionData.subscription_status === 'trialing' || subscriptionData.subscription_status === 'trial',
    subscription_status: (subscriptionData.subscription_status === 'trial' ? 'trialing' : subscriptionData.subscription_status || 'new') as 'trialing' | 'active' | 'canceled' | 'past_due' | 'unpaid' | 'expired' | 'new' | null,
    subscription_tier: subscriptionData.subscription_tier as 'Plus' | 'Premium' | null,
    subscription_end_date: subscriptionData.subscription_end_date,
    trial_end_date: subscriptionData.trial_end_date,
    payment_method_added: subscriptionData.payment_method_added || false,
    stripe_customer_id: subscriptionData.stripe_customer_id,
    isTrialExpired: subscriptionData.subscription_status === 'trial' && subscriptionData.trial_end_date ? new Date(subscriptionData.trial_end_date) < new Date() : false,
    isPastDue: subscriptionData.subscription_status === 'past_due',
    isCanceled: subscriptionData.subscription_status === 'canceled',
    isActive: ['active', 'trialing', 'trial'].includes(subscriptionData.subscription_status || '') && !(subscriptionData.subscription_status === 'trial' && subscriptionData.trial_end_date && new Date(subscriptionData.trial_end_date) < new Date()),
    isOnTrial: subscriptionData.subscription_status === 'trialing' || subscriptionData.subscription_status === 'trial',
    needsPayment: ['past_due', 'unpaid'].includes(subscriptionData.subscription_status || ''),
    isExpired: subscriptionData.subscription_end_date ? new Date(subscriptionData.subscription_end_date) < new Date() : false,
    needsPaymentMethod: (subscriptionData.subscription_status === 'trial' || subscriptionData.subscription_status === 'trialing') && !subscriptionData.payment_method_added,
    isInGracePeriod: subscriptionData.subscription_status === 'grace_period' && subscriptionData.subscription_end_date ? new Date(subscriptionData.subscription_end_date) > new Date() : false,
    hasPaymentIssues: ['past_due', 'canceled'].includes(subscriptionData.subscription_status || '') || (subscriptionData.subscription_status === 'grace_period' && subscriptionData.subscription_end_date ? new Date(subscriptionData.subscription_end_date) <= new Date() : false),
    gracePeriodDays: subscriptionData.subscription_status === 'grace_period' && subscriptionData.subscription_end_date ? Math.max(0, Math.ceil((new Date(subscriptionData.subscription_end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : undefined,
    trialDaysRemaining: (subscriptionData.subscription_status === 'trial' || subscriptionData.subscription_status === 'trialing') && subscriptionData.trial_end_date ? Math.max(0, Math.ceil((new Date(subscriptionData.trial_end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : undefined,
    buttonText: getButtonText(subscriptionData)
  } : null;

  function getButtonText(data: any): string {
    if (!data.subscription_status || data.subscription_status === 'new') return 'Subscribe';

    const now = new Date();
    const trialEnd = data.trial_end_date ? new Date(data.trial_end_date) : null;
    const subscriptionEnd = data.subscription_end_date ? new Date(data.subscription_end_date) : null;

    // Trial states
    if (data.subscription_status === 'trial' || data.subscription_status === 'trialing') {
      if (!data.payment_method_added) {
        return "Add Payment Method";
      }
      if (trialEnd && now > trialEnd) {
        return "Activate Subscription";
      }
      return "Manage Trial";
    }

    // Active subscription
    if (data.subscription_status === 'active') {
      return "Manage Billing";
    }

    // Payment issues
    if (data.subscription_status === 'past_due') {
      return "Update Payment Method";
    }

    // Grace period (canceled but still has access)
    if (data.subscription_status === 'grace_period') {
      if (subscriptionEnd && now < subscriptionEnd) {
        return "Reactivate Subscription";
      }
      return "Renew Subscription";
    }

    // Fully expired or canceled
    if (data.subscription_status === 'canceled' || data.subscription_status === 'trial_expired') {
      return "Reactivate Plan";
    }

    return "Upgrade";
  }

  const createCheckout = async (
    planType: 'Plus',
    billingCycle: 'monthly' | 'annual' = 'monthly'
  ) => {
    if (!user) return;

    setIsCreatingCheckout(true);

    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { planType, billingCycle }
      });

      if (error) {
        console.error('Error creating checkout:', error);
        toast({
          title: "Error",
          description: "Failed to create checkout session",
          variant: "destructive",
        });
        return;
      }

      // Open Stripe checkout in a new tab
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({
        title: "Error",
        description: "Failed to create checkout session",
        variant: "destructive",
      });
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  const openCustomerPortal = async (flowData?: any) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please log in to manage your subscription.",
        variant: "destructive",
      });
      return;
    }

    try {
      // First, try to refresh the session to ensure we have a valid token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        console.error('Session refresh failed:', sessionError);
        toast({
          title: "Session expired",
          description: "Please log in again to manage your subscription.",
          variant: "destructive",
        });
        // Force sign out and redirect to home
        window.location.href = '/';
        return;
      }

      console.log('Opening customer portal with session:', session.user.email);

      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: flowData ? { flow_data: flowData } : undefined
      });

      if (error) {
        console.error('Customer portal error details:', error);
        throw new Error(error.message || 'Failed to create portal session');
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast({
        title: "Error",
        description: `Failed to open customer portal: ${error.message}. Please try logging out and back in.`,
        variant: "destructive",
      });
    }
  };


  // Only check once on initial load if no cached data
  useEffect(() => {
    if (user && !subscriptionData && !isLoading && !requestInProgressRef.current) {
      console.log('🚀 Initial subscription check for new user session');
      checkSubscription(true);
    }
  }, [user, subscriptionData, isLoading, checkSubscription]);

  return {
    subscription,
    loading: isLoading,
    isFetching,
    creditsData: subscriptionData ? {
      credits_left: subscriptionData.credits_left,
      renew_ts: subscriptionData.renew_ts
    } : null,
    // Provide raw subscription data for components that need full access
    rawSubscriptionData: subscriptionData,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
    refreshSubscription: () => refetch(),
    isCreatingCheckout,
  };
};
